import { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

/* ---- Local interfaces for QuoteBuilder state ---- */

export interface NormalizedDefaultAddon {
  DefaultAddonId: number;
  AddonId: number;
  ProviderId: number;
  RegionId: number;
  EnvironmentId: number;
  EnvironmentCode: string;
  ApplyToVMs: boolean;
  ApplyToDatabaseVMs: boolean;
  IsEnabled: number;
}

export interface VMRow {
  id: number;
  name: string;
  cpu: number;
  ram: number;
  osTypeId: number | string;
  environmentId: number;
  disks: (number | null)[];
  quantity: number;
  dailyBackups: number;
  drTestingFrequency: number;
  savedLineCost?: number | null;
}

export interface PaasDatabaseRow {
  id: number;
  databaseTypeId: number | string;
  deploymentType: 'paas';
  cores: number;
  tier: string;
  databaseConfigId: number | string;
  quantity: number;
  dailyBackups: number;
  extraStorageGB: number;
  savedLineCost?: number | null;
}

export interface VmDatabaseRow {
  id: number;
  databaseTypeId: number | string;
  deploymentType: 'vm_based';
  cpu: number;
  ram: number;
  editionId: number | string;
  environmentId: number;
  disks: (number | null)[];
  quantity: number;
  dailyBackups: number;
  savedLineCost?: number | null;
}

export type DatabaseRow = PaasDatabaseRow | VmDatabaseRow;

export interface OtherLineItem {
  id: number;
  itemId: number | string;
  optionId: number | string;
  quantity: number;
  savedLineCost?: number | null;
}

export interface PowerVMRow {
  id: number;
  name: string;
  powerTypeId: number | string;
  environmentId: number;
  cpu: number;
  ram: number;
  quantity: number;
  disks: (number | null)[];
  savedLineCost?: number | null;
}

export interface PowerAddonLineItem {
  id: number;
  powerAddonId: number | string;
  powerAddonOptionId: number | string;
  quantity: number;
  savedLineCost?: number | null;
}

export interface NormalizedPowerDefaultAddon {
  PowerDefaultAddonId: number;
  PowerAddonId: number;
  ProviderId: number;
  RegionId: number;
  EnvironmentId: number;
  EnvironmentCode: string;
  IsEnabled: number;
}

export interface SelectOption {
  value: number | string;
  label: string | number;
  disabled?: boolean;
  cfgId?: number;
}

export interface Totals {
  monthly: number;
  annual: number;
  oneTime: number;
}

/* ---- Style constants ---- */

export const DISK_LIST_SX = { display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflowX: 'auto', scrollbarWidth: 'thin' } as const;
export const HEADER_CELL_SX = { fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', py: 0.375, px: 0.25 } as const;
export const TABLE_CELL_SX = { py: 0.375, px: 0.25, verticalAlign: 'middle' } as const;
export const SPACER_CELL_SX = { width: '100%' } as const;
export const BACKUP_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 4, label: '4' },
  { value: 8, label: '8' },
  { value: 12, label: '12' },
  { value: 24, label: '24' },
  { value: 48, label: '48' },
];

export const DR_TESTING_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 6, label: '6' },
  { value: 12, label: '12' },
];

export const COL = {
  NAME: 220, ENV: 112, CPU: 90, RAM: 90, OS: 190, OS2: 220,
  STORAGE: 260, QTY: 80, BACKUPS: 130, COST: 110, ACT: 50,
} as const;
export const CELL_W = (w: number) => ({ width: w, minWidth: w, maxWidth: w });
export const NO_SPIN_SX = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
} as const;
export const rowErrorSX = (theme: Theme) => ({
  backgroundColor: alpha(theme.palette.error.main, 0.10),
  borderLeft: `4px solid ${theme.palette.error.main}`,
  '&.MuiTableRow-hover:hover': {
    backgroundColor: alpha(theme.palette.error.main, 0.15),
  },
});
